import { InstreamAd } from '@/app_components/common/ads/InStream';
import { EmbedPriority, type ParserRule } from '@/app_lib/htmlParser/inserter/rules/types';
import { baseAdPlacementConfig } from '@/app_lib/utils/ads';
import { cn } from '@/app_lib/utils/utils';

interface EmbedAdSlotsProps {
  noInstream: boolean;
}

function getAdIndex(index: number, frequency: number) {
  return Math.max(0, Math.ceil(index / frequency) - 1); // 0 indexed
}

/**
 * This is essentially a way to find out if we should place an ad at the current index without knowing the ad number.
 * Since we aren't always starting at index 0, when we mod the index by the frequency, we need to account for the first ad placement offset.
 *
 * @param frequency - The interval at which ads should be placed.
 * @param index - The current index to check for ad placement.
 * @returns `true` if the index matches the ad placement rule; otherwise, `false`.
 */
export function isAdPosition(frequency: number, index: number) {
  return index % frequency === baseAdPlacementConfig.firstAdPlacement;
}

// TODO: Mobile ads end up on the same positioning as Nativo (position 13).
export const embedAdSlots = ({ noInstream }: EmbedAdSlotsProps): ParserRule => ({
  /** This is confusing so here's an explanation
   * Since we can't tell if the user is on mobile or desktop in a server component, we have to render the mobile AND desktop ad slot
   * and let the ad component decide if it should show or not based on media queries.
   * The wrong component will get deleted by the media query so it won't take up space.
   */
  insertComponent: ({ nodeTracker }) => {
    const index = nodeTracker.p;
    const frequencyDesktop = baseAdPlacementConfig.desktopAdFrequency;
    const frequencyMobile = baseAdPlacementConfig.mobileAdFrequency;

    const isMobileAd = isAdPosition(baseAdPlacementConfig.mobileAdFrequency, index);
    const isDesktopAd = isAdPosition(baseAdPlacementConfig.desktopAdFrequency, index);

    const mobileAdIndex = getAdIndex(index, frequencyMobile); // 0 indexed
    const desktopAdIndex = getAdIndex(index, frequencyDesktop); // 0 indexed
    const bothFrequencies = isMobileAd && isDesktopAd;

    return (
      <div
        className={cn(
          // We need to reserve space, but only if we know one of the frequencies is going to show
          // There may be an overlap so this will cover that too
          isDesktopAd && !isMobileAd && 'max-md:contents md:min-h-[250px]',
          isMobileAd && !isDesktopAd && 'max-md:min-h-[250px] md:contents',
          bothFrequencies && 'min-h-[250px]',
          // this is a bit of a hack because sometimes the ads have no visible content until they load
          'isolate z-body-popover [&>.contents]:contents', // some ads are going over the leaderboard ad
        )}
        key={`ad-embed-${index}`}
      >
        <InstreamAd
          className='min-h-[250px] flex items-center'
          desktopAdIndex={desktopAdIndex}
          index={mobileAdIndex}
          isDesktopAd={isDesktopAd}
          isMobileAd={isMobileAd}
          placementName='InContent'
        />
      </div>
    );
  },
  insertPosition: 'after',
  match: ({ nodeTracker, node }) => {
    const isMobileAd = isAdPosition(baseAdPlacementConfig.mobileAdFrequency, nodeTracker.p);
    const isDesktopAd = isAdPosition(baseAdPlacementConfig.desktopAdFrequency, nodeTracker.p);
    return !noInstream && node.type === 'p' && (isMobileAd || isDesktopAd);
  },
  priority: EmbedPriority.High,
});
